import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { withRouter, Redirect } from 'react-router-dom';
import { useQuery, useLazyQuery } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { Page, Row, Column } from 'components/shared/Layout';
import { Spinner } from 'admin-components/components';
import AgentPortalError from 'errors/AgentPortalError';
import { ACTION_CODES } from 'constants/errors';
import { VOUCHER_TYPE_DISCOUNT, VoucherStatus } from 'constants/vouchers';
import { useManagedQuery } from 'utils/managedHooks';
import { GET_USER } from 'store/queries';
import DiscountCodeDetails from './DiscountCodeDetails';
import GET_VOUCHER_DETAILS from './queries/GET_VOUCHER_DETAILS';
import { VOUCHER_DESCRIPTION } from 'pages/GenerateDiscountCode/utils/constant';
import AddVoucherNoteForm from 'pages/VoucherDetails/AddVoucherNoteForm';
import VoucherNotes from 'pages/VoucherDetails/VoucherNotes';
import GET_VOUCHER_NOTES from 'pages/VoucherDetails/queries/GET_VOUCHER_NOTES';
import { StyledH2 } from 'pages/VoucherDetails/VoucherDetails.styles';
import VoucherTransactionsTable from 'pages/VoucherDetails/VoucherTransactionsTable';
import { BE_MY_GUEST_VOUCHER_DESCRIPTION, SILVER_VOUCHER_DESCRIPTION } from 'constants/vouchers';

const DiscountCodeDetailsWrapper = ({
  match: {
    params: { discountCodeAlias }
  }
}) => {
  const {
    data: {
      me: {
        canEnableVouchers,
        canDisableVouchers,
        canUpdateVouchers,
        canManageVouchers,
        canRetrieveVouchers,
        canEnableSilverVoucher,
        canDisableSilverVoucher
      }
    }
  } = useManagedQuery(GET_USER);

  const {
    loading: voucherDetailsLoading,
    data: voucherDetailsData,
    error: voucherDetailsError
  } = useQuery(GET_VOUCHER_DETAILS, {
    variables: {
      voucherAlias: discountCodeAlias,
      voucherType: VOUCHER_TYPE_DISCOUNT
    },
    fetchPolicy: 'network-only'
  });

  const [getDiscountNotesQuery, { data: discountNotesData, error: discountNotesError, refetch: refetchDiscountNotes }] =
    useLazyQuery(GET_VOUCHER_NOTES);

  const {
    findVoucherByReferenceAndMarket: {
      createdFor,
      businessAccount,
      reference,
      type,
      valueType,
      currency,
      value,
      status,
      customerEmail,
      firstName,
      lastName,
      salesPeriodStart,
      salesPeriodEnd,
      travelPeriodStart,
      travelPeriodEnd,
      travelDays,
      createdBy,
      createdAt,
      updatedBy,
      updatedAt,
      isActive,
      activatedAt,
      inactivatedAt,
      replacedByVoucher,
      travelPeriodExceptions,
      salesChannelRestrictions,
      countryRestrictions,
      productRestrictions,
      settings,
      stationRestrictions,
      maxTotalDiscountAmount,
      minBookingValue,
      maxBookingValue,
      payments,
      alias
    } = {}
  } = voucherDetailsData || {};

  useEffect(() => {
    if (alias) {
      getDiscountNotesQuery({
        variables: {
          voucherAlias: alias
        }
      });
    }
  }, [alias, getDiscountNotesQuery]);

  if (!canManageVouchers) {
    return <Redirect to="/" />;
  }

  if (!canRetrieveVouchers) {
    return <Redirect to="/" />;
  }

  const loading = voucherDetailsLoading;
  const error = voucherDetailsError;

  const { getVoucherNotes: discountNotes } = discountNotesData || {};

  if (loading) {
    return (
      <Row span={16} height="100vh">
        <Column justifyContent="center" alignItems="center">
          <Spinner />
        </Column>
      </Row>
    );
  }

  if (error) {
    throw new AgentPortalError(error, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: '/discountCodes/manage'
    });
  }

  const canDisableVoucher = VoucherStatus[status] === VoucherStatus.ACTIVE && canDisableVouchers;
  const canEnableVoucher = VoucherStatus[status] === VoucherStatus.INACTIVE && canEnableVouchers && !replacedByVoucher;

  const canEnableSilverVouchers = VoucherStatus[status] === VoucherStatus.INACTIVE && canEnableSilverVoucher;
  const canDisableSilverVouchers = VoucherStatus[status] === VoucherStatus.ACTIVE && canDisableSilverVoucher;

  const isNewVoucherType = type.includes(BE_MY_GUEST_VOUCHER_DESCRIPTION) || type.includes(SILVER_VOUCHER_DESCRIPTION);

  return (
    <Page columns={20}>
      <Row span={18} start={2}>
        <h1>
          <FormattedMessage id="discountCodes.manageDiscountCode" />
        </h1>
        <DiscountCodeDetails
          createdFor={createdFor}
          businessAccount={businessAccount}
          firstName={firstName}
          lastName={lastName}
          email={customerEmail}
          reference={reference}
          type={type}
          valueType={valueType}
          voucherCurrency={currency}
          voucherOriginalValue={value}
          voucherStatus={status}
          salesPeriodStart={salesPeriodStart}
          salesPeriodEnd={salesPeriodEnd}
          travelPeriodStart={travelPeriodStart}
          travelPeriodEnd={travelPeriodEnd}
          travelDays={travelDays}
          createdBy={createdBy}
          createdAt={createdAt}
          updatedBy={updatedBy}
          updatedAt={updatedAt}
          activatedAt={activatedAt}
          inactivatedAt={inactivatedAt}
          canDisableVoucher={canDisableVoucher}
          canEnableVoucher={canEnableVoucher}
          canUpdateVoucher={canUpdateVouchers}
          travelPeriodExceptions={travelPeriodExceptions}
          salesChannelRestrictions={salesChannelRestrictions}
          countryRestrictions={countryRestrictions}
          productRestrictions={productRestrictions}
          isActive={isActive}
          settings={settings}
          stationRestrictions={stationRestrictions}
          maxTotalDiscountAmount={maxTotalDiscountAmount}
          minBookingValue={minBookingValue}
          maxBookingValue={maxBookingValue}
          payments={payments}
          canUpdateSilverVouchers={(canEnableSilverVoucher || canDisableSilverVoucher) && type === VOUCHER_DESCRIPTION.SILVER_DISCOUNT}
          canEnableSilverVouchers={canEnableSilverVouchers}
          canDisableSilverVouchers={canDisableSilverVouchers}
          voucherAlias={alias}
        />
        <StyledH2>
          <FormattedMessage id="notes.notes" />
        </StyledH2>
        <AddVoucherNoteForm voucherAlias={alias} refetchVoucherNotes={refetchDiscountNotes} />
        <VoucherNotes
          notes={discountNotes?.map((note) => ({
            id: note.id,
            createdAt: note.createdAt,
            author: note.createdBy,
            content: note.content
          }))}
          hasError={Boolean(discountNotesError)}
        />
        {isNewVoucherType && (
          <>
            <StyledH2>
              <FormattedMessage id="vouchers.transactions" />
            </StyledH2>
            <VoucherTransactionsTable
              transactions={payments.map((payment) => ({
                amount: payment.amount,
                currency: payment.currency,
                status: payment.status,
                pnr: payment.bookingReference
              }))}
            />
          </>
        )}
      </Row>
    </Page>
  );
};

DiscountCodeDetailsWrapper.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      discountCodeAlias: PropTypes.string.isRequired
    }).isRequired
  }).isRequired
};

export default withRouter(DiscountCodeDetailsWrapper);
